import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import api from '../../../services/api';
import { locationsService } from '../../../services/locationsService';

const CorrectionsTab = () => {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const defaultFrom = toISO(firstDay);
  const defaultTo = toISO(now);

  const [dateFrom, setDateFrom] = useState(defaultFrom);
  const [dateTo, setDateTo] = useState(defaultTo);
  const [locationId, setLocationId] = useState('');
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ corrections: [], count: 0, total_brutto: 0, total_netto: 0, total_vat: 0 });

  useEffect(() => {
    locationsService.getLocations().then(res => {
      const arr = res?.data?.locations || res?.locations || res?.data || [];
      setLocations(Array.isArray(arr) ? arr : []);
    }).catch(err => console.error('Błąd pobierania lokalizacji:', err));
  }, []);

  const buildRequestParams = useCallback((extra = {}) => {
    // Przekazujemy przez config.params (nie URL) zeby axios interceptor
    // zobaczyl location_id juz ustawione i NIE nadpisal selectedLocation z localStorage.
    const p = { date_from: dateFrom, date_to: dateTo, ...extra };
    if (locationId) {
      p.location_id = locationId;
    } else {
      p.location_id = 'all';
      p.all_locations = '1';
    }
    return p;
  }, [dateFrom, dateTo, locationId]);

  const loadCorrections = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildRequestParams();
      const response = await api.get('/admin/corrections', { params });
      console.log('[Korekty] GET /admin/corrections', params, '→', response.status, response.data);
      if (response.data?.success) {
        setData(response.data.data || { corrections: [], count: 0 });
      } else {
        toast.error(response.data?.error || 'Błąd pobierania korekt');
      }
    } catch (err) {
      console.error('[Korekty] error', err);
      toast.error(err.response?.data?.error || err.message || 'Błąd ładowania korekt');
    } finally {
      setLoading(false);
    }
  }, [buildRequestParams]);

  useEffect(() => {
    loadCorrections();
  }, [loadCorrections]);

  const quickRange = (type) => {
    const d = new Date();
    if (type === 'today') {
      setDateFrom(toISO(d)); setDateTo(toISO(d));
    } else if (type === 'month') {
      setDateFrom(toISO(new Date(d.getFullYear(), d.getMonth(), 1))); setDateTo(toISO(d));
    } else if (type === 'prev_month') {
      const f = new Date(d.getFullYear(), d.getMonth() - 1, 1);
      const t = new Date(d.getFullYear(), d.getMonth(), 0);
      setDateFrom(toISO(f)); setDateTo(toISO(t));
    } else if (type === 'year') {
      setDateFrom(toISO(new Date(d.getFullYear(), 0, 1))); setDateTo(toISO(d));
    }
  };

  const downloadFile = async (format) => {
    try {
      const params = buildRequestParams({ format });
      const response = await api.get('/admin/corrections', { params, responseType: 'blob' });
      const blob = new Blob([response.data], {
        type: format === 'pdf' ? 'application/pdf' : 'text/csv'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const locSuffix = locationId ? `_loc${locationId}` : '_all';
      a.download = `korekty_${dateFrom}_${dateTo}${locSuffix}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Pobrano ${format.toUpperCase()}`);
    } catch (err) {
      toast.error(`Błąd eksportu ${format.toUpperCase()}`);
    }
  };

  // Bulk - wszystkie protokoly korekt naraz (ZIP lub jeden merged PDF)
  const downloadBulkPdf = async (bulkFormat) => {
    const label = bulkFormat === 'zip' ? 'ZIP korekt' : 'PDF (scalony)';
    const toastId = toast.loading(`Generowanie ${label}... (moze potrwac do 1 min)`);
    try {
      const params = buildRequestParams({ format: bulkFormat });
      const response = await api.get('/admin/corrections/bulk-pdf', {
        params,
        responseType: 'blob',
        timeout: 180000,  // 3 minuty (bulk moze byc duzy)
      });
      const mime = bulkFormat === 'zip' ? 'application/zip' : 'application/pdf';
      const ext = bulkFormat === 'zip' ? 'zip' : 'pdf';
      const suffix = bulkFormat === 'merged' ? '_merged' : '';
      const blob = new Blob([response.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const locSuffix = locationId ? `_loc${locationId}` : '_all';
      a.download = `korekty_${dateFrom}_${dateTo}${locSuffix}${suffix}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Pobrano ${label}`, { id: toastId });
    } catch (err) {
      // Blob error response - sparsuj jesli JSON
      let errMsg = err?.message || 'Nieznany blad';
      if (err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          errMsg = json?.error || json?.message || errMsg;
        } catch { /* nie JSON, zostaw err.message */ }
      }
      toast.error(`Blad pobierania ${label}: ${errMsg}`, { id: toastId, duration: 8000 });
    }
  };

  const locName = (loc_id) => {
    const l = locations.find(x => x.id === loc_id);
    return l ? l.nazwa : `ID ${loc_id}`;
  };

  return (
    <div style={{ padding: '1rem' }}>
      <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>📋 Korekty paragonów</h3>

      {/* Filtry */}
      <div style={{
        display: 'flex', gap: '1rem', alignItems: 'end', marginBottom: '1rem',
        padding: '1rem', backgroundColor: 'var(--bg)', borderRadius: '4px',
        border: '1px solid var(--border)', flexWrap: 'wrap'
      }}>
        <div>
          <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', marginBottom: '4px' }}>
            Data od
          </label>
          <input
            type="date"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            style={{ padding: '6px 10px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)', color: 'var(--text)' }}
          />
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', marginBottom: '4px' }}>
            Data do
          </label>
          <input
            type="date"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            style={{ padding: '6px 10px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)', color: 'var(--text)' }}
          />
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', marginBottom: '4px' }}>
            Szybki zakres
          </label>
          <div style={{ display: 'flex', gap: '4px' }}>
            <button onClick={() => quickRange('today')} style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)', color: 'var(--text-secondary)', cursor: 'pointer' }}>Dziś</button>
            <button onClick={() => quickRange('month')} style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)', color: 'var(--text-secondary)', cursor: 'pointer' }}>Ten mies.</button>
            <button onClick={() => quickRange('prev_month')} style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)', color: 'var(--text-secondary)', cursor: 'pointer' }}>Poprz. mies.</button>
            <button onClick={() => quickRange('year')} style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)', color: 'var(--text-secondary)', cursor: 'pointer' }}>Rok</button>
          </div>
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', marginBottom: '4px' }}>
            Lokalizacja
          </label>
          <select
            value={locationId}
            onChange={e => setLocationId(e.target.value)}
            style={{ padding: '6px 10px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)', color: 'var(--text)', maxWidth: '100%' }}
          >
            <option value="">Wszystkie lokalizacje</option>
            {locations.map(l => (
              <option key={l.id} value={l.id}>{l.nazwa} ({l.kod_lokalizacji})</option>
            ))}
          </select>
        </div>

        <button
          onClick={loadCorrections}
          disabled={loading}
          style={{
            padding: '6px 12px', fontSize: '12px', backgroundColor: 'var(--info)',
            color: '#fff', border: 'none', cursor: 'pointer'
          }}
        >
          🔄 Odśwież
        </button>

        <div style={{ flex: 1 }} />

        <button
          onClick={() => downloadFile('csv')}
          disabled={loading || data.count === 0}
          style={{
            padding: '6px 12px', fontSize: '12px', backgroundColor: 'var(--success)',
            color: '#fff', border: 'none',
            cursor: data.count === 0 ? 'not-allowed' : 'pointer',
            opacity: data.count === 0 ? 0.5 : 1
          }}
        >
          📊 CSV
        </button>

        <button
          onClick={() => downloadFile('pdf')}
          disabled={loading || data.count === 0}
          style={{
            padding: '6px 12px', fontSize: '12px', backgroundColor: 'var(--danger)',
            color: '#fff', border: 'none',
            cursor: data.count === 0 ? 'not-allowed' : 'pointer',
            opacity: data.count === 0 ? 0.5 : 1
          }}
          title="Raport zbiorczy (lista + sumy) w formacie PDF"
        >
          📄 PDF
        </button>

        {/* Bulk - wszystkie protokoly korekt */}
        <button
          onClick={() => downloadBulkPdf('zip')}
          disabled={loading || data.count === 0}
          style={{
            padding: '6px 12px', fontSize: '12px', backgroundColor: 'var(--warning)',
            color: '#fff', border: 'none',
            cursor: data.count === 0 ? 'not-allowed' : 'pointer',
            opacity: data.count === 0 ? 0.5 : 1
          }}
          title={`Pobierz ${data.count} osobnych PDF-ow (po jednym per korekta) jako ZIP`}
        >
          📦 ZIP ({data.count} PDF)
        </button>

        <button
          onClick={() => downloadBulkPdf('merged')}
          disabled={loading || data.count === 0}
          style={{
            padding: '6px 12px', fontSize: '12px', backgroundColor: 'var(--purple)',
            color: '#fff', border: 'none',
            cursor: data.count === 0 ? 'not-allowed' : 'pointer',
            opacity: data.count === 0 ? 0.5 : 1
          }}
          title={`Wszystkie ${data.count} protokolow korekt scalone w jeden wielostronicowy PDF`}
        >
          📑 PDF scalony
        </button>
      </div>

      {/* Podsumowanie */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem',
        marginBottom: '1rem'
      }}>
        <div style={{ padding: '1rem', backgroundColor: 'var(--bg)', borderRadius: '4px', border: '1px solid var(--border)' }}>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Liczba korekt</div>
          <div style={{ fontSize: '24px', fontWeight: '600' }}>{data.count}</div>
        </div>
        <div style={{ padding: '1rem', backgroundColor: 'var(--bg)', borderRadius: '4px', border: '1px solid var(--border)' }}>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Brutto</div>
          <div style={{ fontSize: '20px', fontWeight: '600', color: 'var(--danger)' }}>
            {Number(data.total_brutto || 0).toFixed(2)} zł
          </div>
        </div>
        <div style={{ padding: '1rem', backgroundColor: 'var(--bg)', borderRadius: '4px', border: '1px solid var(--border)' }}>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Netto</div>
          <div style={{ fontSize: '20px', fontWeight: '600' }}>
            {Number(data.total_netto || 0).toFixed(2)} zł
          </div>
        </div>
        <div style={{ padding: '1rem', backgroundColor: 'var(--bg)', borderRadius: '4px', border: '1px solid var(--border)' }}>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>VAT</div>
          <div style={{ fontSize: '20px', fontWeight: '600' }}>
            {Number(data.total_vat || 0).toFixed(2)} zł
          </div>
        </div>
      </div>

      {/* Tabela */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
          Ładowanie...
        </div>
      ) : data.count === 0 ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
          Brak korekt w wybranym okresie
        </div>
      ) : (
        <div className="admin-table-wrap" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--bg)', borderBottom: '2px solid var(--border)' }}>
                <th style={{ padding: '0.5rem', textAlign: 'left', whiteSpace: 'nowrap' }}>Nr korekty</th>
                <th style={{ padding: '0.5rem', textAlign: 'left', whiteSpace: 'nowrap' }}>Paragon źródłowy</th>
                <th style={{ padding: '0.5rem', textAlign: 'left', whiteSpace: 'nowrap' }}>Data</th>
                <th style={{ padding: '0.5rem', textAlign: 'left' }}>Kasjer</th>
                <th className="col-hide-mobile" style={{ padding: '0.5rem', textAlign: 'left' }}>Lokalizacja</th>
                <th style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>Brutto</th>
                <th className="col-hide-mobile" style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>Netto</th>
                <th className="col-hide-mobile" style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>VAT</th>
                <th className="col-hide-mobile" style={{ padding: '0.5rem', textAlign: 'left' }}>Forma pł.</th>
                <th className="col-hide-mobile" style={{ padding: '0.5rem', textAlign: 'center' }}>Poz.</th>
                <th className="col-hide-mobile" style={{ padding: '0.5rem', textAlign: 'left' }}>Powód</th>
              </tr>
            </thead>
            <tbody>
              {data.corrections.map(c => (
                <tr key={c.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '0.5rem', fontWeight: '600', whiteSpace: 'nowrap' }}>{c.numer_zwrotu}</td>
                  <td style={{ padding: '0.5rem', whiteSpace: 'nowrap' }}>{c.numer_paragonu || '-'}</td>
                  <td style={{ padding: '0.5rem', whiteSpace: 'nowrap' }}>{c.data_zwrotu} {c.czas_zwrotu || ''}</td>
                  <td style={{ padding: '0.5rem' }}>{c.kasjer_login}</td>
                  <td className="col-hide-mobile" style={{ padding: '0.5rem' }}>{c.location_nazwa || locName(c.location_id)}</td>
                  <td style={{ padding: '0.5rem', textAlign: 'right', color: 'var(--danger)', fontWeight: '600', whiteSpace: 'nowrap' }}>
                    {Number(c.suma_zwrotu_brutto || 0).toFixed(2)} zł
                  </td>
                  <td className="col-hide-mobile" style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>{Number(c.suma_zwrotu_netto || 0).toFixed(2)}</td>
                  <td className="col-hide-mobile" style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>{Number(c.suma_zwrotu_vat || 0).toFixed(2)}</td>
                  <td className="col-hide-mobile" style={{ padding: '0.5rem' }}>{c.forma_platnosci}</td>
                  <td className="col-hide-mobile" style={{ padding: '0.5rem', textAlign: 'center' }}>{c.items_count}</td>
                  <td className="col-hide-mobile" style={{ padding: '0.5rem', fontSize: '11px', color: 'var(--text-muted)', maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={c.powod_zwrotu}>
                    {c.powod_zwrotu || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CorrectionsTab;
